const INIT_PROBE_TIMEOUT_MS = 2500;
const SNAP_TOKEN_PLACEHOLDER = "PUT_STAGING_SNAP_API_TOKEN_HERE";

let activeInitProbeAttemptId = 0;
let initProbeInFlight = false;
/** @type {{ cancelled: boolean } | null} */
let activeRunState = null;

function isSnapCameraKitInitProbeEnabled() {
  const raw = String(process.env.REACT_APP_SNAP_CALL_FILTER_INIT_PROBE || "")
    .trim()
    .toLowerCase();
  return raw === "true";
}

function isProbeSnapTokenConfigured() {
  const token = String(process.env.REACT_APP_SNAP_API_TOKEN || "").trim();
  return Boolean(token && token !== SNAP_TOKEN_PLACEHOLDER);
}

/**
 * Invalidate in-flight CameraKit init probe (call end, cleanup, unmount).
 * @param {string} [_reason]
 */
export function invalidateSnapCameraKitInitProbe(_reason = "unknown") {
  activeInitProbeAttemptId += 1;
  if (activeRunState) {
    activeRunState.cancelled = true;
  }
  initProbeInFlight = false;
}

function isStaleInitProbeAttempt(attemptId) {
  return attemptId !== activeInitProbeAttemptId;
}

function logInitProbeAborted() {
  console.log("[snapInitProbe] failed reason=aborted");
}

function throwIfInitProbeCancelled(runState, attemptId) {
  if (runState.cancelled || isStaleInitProbeAttempt(attemptId)) {
    throw Object.assign(new Error("aborted"), { code: "ABORTED" });
  }
}

/**
 * Bootstrap-only CameraKit init probe — dynamic import when flag enabled.
 * Never throws; never touches Zego, lens, session, or camera.
 * @param {{ callId?: string }} [_params]
 */
export async function runSnapCameraKitInitProbe(_params = {}) {
  try {
    const enabled = isSnapCameraKitInitProbeEnabled();
    console.log(`[snapInitProbe] enabled=${enabled}`);
    if (!enabled) {
      return;
    }

    const tokenConfigured = isProbeSnapTokenConfigured();
    console.log(`[snapInitProbe] tokenConfigured=${tokenConfigured}`);
    if (!tokenConfigured) {
      console.log("[snapInitProbe] failed reason=missing_token");
      return;
    }

    if (initProbeInFlight) {
      return;
    }

    const attemptId = activeInitProbeAttemptId;
    const runState = { cancelled: false };
    activeRunState = runState;
    initProbeInFlight = true;
    const startedAt = performance.now();
    console.log("[snapInitProbe] started");

    const apiToken = String(process.env.REACT_APP_SNAP_API_TOKEN || "").trim();
    const operationPromise = (async () => {
      let bootstrapCameraKit;
      try {
        ({ bootstrapCameraKit } = await import("@snap/camera-kit"));
      } catch {
        throw Object.assign(new Error("import_failed"), { code: "IMPORT_FAILED" });
      }
      throwIfInitProbeCancelled(runState, attemptId);
      return bootstrapCameraKit({ apiToken, logLevel: "warn" });
    })();

    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        runState.cancelled = true;
        reject(Object.assign(new Error("timeout"), { code: "TIMEOUT" }));
      }, INIT_PROBE_TIMEOUT_MS);
    });

    try {
      await Promise.race([operationPromise, timeoutPromise]);
      operationPromise.catch(() => {});

      if (isStaleInitProbeAttempt(attemptId) || runState.cancelled) {
        logInitProbeAborted();
        return;
      }

      const ms = Math.round(performance.now() - startedAt);
      console.log(`[snapInitProbe] success ms=${ms}`);
    } catch (err) {
      operationPromise.catch(() => {});

      if (err?.code === "TIMEOUT") {
        console.log(`[snapInitProbe] timeout ms=${INIT_PROBE_TIMEOUT_MS}`);
        return;
      }

      if (err?.code === "ABORTED" || isStaleInitProbeAttempt(attemptId) || runState.cancelled) {
        logInitProbeAborted();
        return;
      }

      if (err?.code === "IMPORT_FAILED") {
        console.log("[snapInitProbe] failed reason=import_failed");
        return;
      }

      console.log("[snapInitProbe] failed reason=bootstrap_error");
    } finally {
      clearTimeout(timeoutId);
      if (activeRunState === runState) {
        activeRunState = null;
      }
      initProbeInFlight = false;
    }
  } catch {
    initProbeInFlight = false;
    activeRunState = null;
  }
}
